/**
 * The owner org of something a superuser creates (JSS#959): Create Site and
 * Create Event. Every org-owned row has a real owner org (JSS#849). An org
 * user's create is their org's, so they get no picker. A superuser picks the
 * owner from the orgs the org switcher lists, starting at the switcher's
 * pick. From the all-orgs view they used to get no picker, and the create
 * was refused with a raw `API error 400: {…}`.
 *
 * The create sends the pick as `orgId` and acts in it for that one call
 * (`actingOrgId` in api/client.ts), so the service sees the same org in
 * X-User-Org as in the body and never answers org_mismatch.
 */
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { meApi, getOrgOverride } from '@/api/client';
import { parseApiError } from '@/lib/apiError';

export interface OwnerOrgPicker {
  /** The superuser's choice; undefined for an org user, whose org is implied. */
  orgId: string | undefined;
  /** A superuser has not chosen an org the list holds: the create must wait. */
  missing: boolean;
  /** Only a superuser gets the picker. */
  shown: boolean;
  select: (orgId: string) => void;
  options: { id: string; name: string }[] | undefined;
  isLoading: boolean;
  isError: boolean;
}

export function useOwnerOrgPicker(open: boolean, isSuperUser: boolean): OwnerOrgPicker {
  const [pickedId, setPickedId] = useState('');

  // The org switcher's list (Header.tsx, same query): every org, for a superuser.
  const organizations = useQuery({
    queryKey: ['my-organizations'],
    queryFn: () => meApi.organizations(),
    enabled: open && isSuperUser,
    staleTime: 60_000,
  });

  // Each time the dialog opens, the owner starts at the switcher's pick
  // (none in the all-orgs view: the superuser must choose).
  useEffect(() => {
    if (open && isSuperUser) setPickedId(getOrgOverride() ?? '');
  }, [open, isSuperUser]);

  // A pick the list does not hold (a stale or deleted org) is no choice.
  const owner = organizations.data?.find((o) => o.id === pickedId);

  return {
    orgId: isSuperUser ? owner?.id : undefined,
    missing: isSuperUser && !owner,
    shown: isSuperUser,
    select: setPickedId,
    options: organizations.data,
    isLoading: organizations.isLoading,
    isError: organizations.isError,
  };
}

/** The picker itself, rendered only for a superuser. Styled by the dialog it sits in. */
export function OwnerOrgSelect({
  id,
  picker,
  noun,
  labelClassName,
  selectClassName,
  hintClassName,
}: {
  id: string;
  picker: OwnerOrgPicker;
  /** What is being created: "site", "event". */
  noun: string;
  labelClassName: string;
  selectClassName: string;
  hintClassName: string;
}) {
  if (!picker.shown) return null;
  return (
    <div>
      <label htmlFor={id} className={labelClassName}>
        Organization *
      </label>
      <select
        id={id}
        value={picker.orgId ?? ''}
        onChange={(e) => picker.select(e.target.value)}
        required
        className={selectClassName}
      >
        <option value="" disabled>
          {picker.isLoading ? 'Loading organizations…' : 'Choose an organization'}
        </option>
        {picker.options?.map((o) => (
          <option key={o.id} value={o.id}>{o.name}</option>
        ))}
      </select>
      <p className={hintClassName}>
        {picker.isError
          ? 'Could not load the organizations.'
          : `The ${noun} belongs to this organization. Its members see and manage it.`}
      </p>
    </div>
  );
}

/**
 * A refused create as a sentence, not `API error 400: {…}` (JSS#959). The
 * owner refusals get a sentence; any other refusal shows the server's message.
 */
export function ownerOrgErrorMessage(err: unknown, noun: string): string {
  const parsed = parseApiError(err);
  switch (parsed.code) {
    case 'org_required':
      return `Choose the organization that owns this ${noun}. If you chose one, it no longer exists: pick another.`;
    case 'org_mismatch':
      return `This ${noun} would belong to a different organization than the one you are acting in. Switch to that organization in the org switcher, then create the ${noun}.`;
    default:
      return parsed.message;
  }
}
